import React, { useEffect, useRef, useState } from 'react';

// A screenshot-free showcase for projects whose product is a system rather
// than a screen: agent teams, pipelines, tooling. It plays one run through the
// system's stages, pauses where a human has to decide, and lists those gates.

const STEP_MS = 1500;
const GATE_MS = 2600;
const REST_MS = 2400;
const LOG_LINES = 5;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SystemShowcase = ({ title, showcase }) => {
  const { task, stages } = showcase;
  // `step` is how many stages have started; stages.length means the run finished.
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [visible, setVisible] = useState(true);
  const rootRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setPlaying(false);
      setStep(stages.length);
    }
  }, [stages.length]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      threshold: 0.2,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!playing || !visible) return undefined;
    const current = stages[step - 1];
    const delay =
      step >= stages.length ? REST_MS : current && current.gate ? GATE_MS : STEP_MS;
    const timer = setTimeout(() => {
      setStep((s) => (s >= stages.length ? 0 : s + 1));
    }, delay);
    return () => clearTimeout(timer);
  }, [playing, visible, step, stages]);

  const active = step - 1;
  const finished = step >= stages.length;
  const waiting = !finished && stages[active] && stages[active].gate;
  const log = stages
    .slice(0, step)
    .map((stage, idx) => ({ idx, stage }))
    .slice(-LOG_LINES);

  const stateOf = (idx) => {
    if (finished || idx < active) return 'done';
    if (idx === active) return stages[idx].gate ? 'gate' : 'active';
    return 'idle';
  };

  const toggle = () => {
    if (finished && !playing) setStep(0);
    setPlaying((p) => !p);
  };

  return (
    <div className="system-showcase" ref={rootRef}>
      <div className="system-run">
        <div className="system-run-head">
          <span className="system-run-label">run</span>
          <span className="system-run-task">{task}</span>
          <button
            type="button"
            className="system-run-toggle interactive-only"
            onClick={toggle}
            aria-label={playing ? `Pause the ${title} run` : `Play the ${title} run`}
          >
            {playing ? 'Pause' : finished ? 'Replay' : 'Play'}
          </button>
        </div>

        <ol className="system-pipeline" aria-label={`${title} pipeline`}>
          {stages.map((stage, idx) => (
            <li
              key={idx}
              className={`system-stage system-stage--${stateOf(idx)}${
                stage.gate ? ' system-stage--has-gate' : ''
              }`}
              aria-current={idx === active && !finished ? 'step' : undefined}
            >
              <span className="system-node" aria-hidden="true">
                {stage.gate ? '◆' : idx + 1}
              </span>
              <span className="system-stage-text">
                <span className="system-stage-label">{stage.label}</span>
                <span className="system-stage-note">{stage.note}</span>
              </span>
              {stage.lanes && (
                <span className="system-lanes" aria-label={`${stage.lanes.length} parallel tracks`}>
                  {stage.lanes.map((lane) => (
                    <span className="system-lane" key={lane}>
                      {lane}
                    </span>
                  ))}
                </span>
              )}
            </li>
          ))}
        </ol>

        <div className="system-log" aria-live="polite" aria-label={`${title} run log`}>
          {log.length === 0 && <p className="system-log-line system-log-line--idle">waiting for a task…</p>}
          {log.map(({ idx, stage }) => (
            <p
              key={`${idx}-${stage.label}`}
              className={`system-log-line${
                idx === active && waiting ? ' system-log-line--gate' : ''
              }`}
            >
              <span className="system-log-who">{stage.label.toLowerCase()}</span>
              {stage.log}
            </p>
          ))}
          {finished && <p className="system-log-line system-log-line--done">shipped. watching for regressions.</p>}
        </div>
      </div>
    </div>
  );
};

// The decisions a person keeps, listed beside the run.
export const HumanGates = ({ title, gates }) => (
  <div className="system-gates">
    <p className="system-gates-title">Your decisions</p>
    <ol className="system-gates-list" aria-label={`${title} human gates`}>
      {gates.map((gate, idx) => (
        <li className="system-gate" key={gate}>
          <span className="system-gate-idx">{String(idx + 1).padStart(2, '0')}</span>
          {gate}
        </li>
      ))}
    </ol>
  </div>
);

export default SystemShowcase;
